import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const ROLES = ['owner', 'admin', 'editor', 'operator', 'finance', 'viewer'] as const;
type Role = typeof ROLES[number];

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface TenantUser {
  id: string;
  tenantId: string;
  userId: string;
  role: Role;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    externalAuthId?: string;
  };
  tenant?: {
    id: string;
    name: string;
    slug: string;
  };
}

import { useTheme } from '@/hooks/useTheme';

export default function TenantUsersPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Load tenants on mount
  useEffect(() => {
    const loadTenants = async () => {
      setTenantsLoading(true);
      try {
        const data = await api.get<Tenant[]>('/tenants');
        setTenants(data);
        if (data.length > 0) {
          setSelectedTenant(data[0]);
        }
      } catch (err) {
        addToast(getApiErrorMessage(err, 'Failed to load tenants'), 'error');
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, []);

  // Load users whenever selected tenant changes
  const loadUsers = useCallback(async (tenant: Tenant) => {
    setUsersLoading(true);
    setUsers([]);
    try {
      const data = await api.get<TenantUser[]>(`/tenants/${tenant.id}/users`);
      setUsers(data);
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to load users'), 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (selectedTenant) {
      loadUsers(selectedTenant);
    }
  }, [selectedTenant, loadUsers]);

  const handleTenantSelect = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setSearch('');
  };

  const handleRoleChange = async (tenantUserId: string, newRole: Role) => {
    setChangingRole(tenantUserId);
    try {
      await api.patch(`/tenant-users/${tenantUserId}/role`, { role: newRole });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === tenantUserId ? { ...u, role: newRole, updatedAt: new Date().toISOString() } : u,
        ),
      );
      addToast('Role updated successfully', 'success');
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to update role'), 'error');
    } finally {
      setChangingRole(null);
    }
  };

  const getRoleBadgeStyle = (role: Role) => {
    const map: Record<Role, { bg: string; color: string }> = {
      owner: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      admin: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      editor: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      operator: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      finance: { bg: 'rgba(107,107,112,0.15)', color: '#4A4A50' },
      viewer: { bg: 'rgba(107,107,112,0.08)', color: '#6B6B70' },
    };
    return map[role] ?? map.viewer;
  };

  const filtered = users.filter(
    (u) =>
      (u.user.fullName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.user.email ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <DashboardLayout breadcrumb={['Administration', 'Tenant Users']}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            Tenant Users
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B6B70' }}>
            Select a tenant to view and manage its users
          </p>
        </div>
      </div>

      <div className="flex gap-5">
        {/* Tenant selector sidebar */}
        <div
          className="w-56 shrink-0 rounded-xl overflow-hidden"
          style={{ border: '1px solid #E5E5E8', background: '#fff', alignSelf: 'flex-start' }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #E5E5E8' }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>
              Tenants
            </span>
          </div>
          {tenantsLoading ? (
            <div className="p-6 text-center">
              <i className="ri-loader-4-line animate-spin text-lg" style={{ color: '#6B6B70' }}></i>
            </div>
          ) : tenants.length === 0 ? (
            <div className="p-4 text-xs text-center" style={{ color: '#6B6B70' }}>No tenants found</div>
          ) : (
            <ul>
              {tenants.map((t) => {
                const isActive = selectedTenant?.id === t.id;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => handleTenantSelect(t)}
                      className="w-full text-left px-4 py-3 text-sm transition-colors cursor-pointer"
                      style={{
                        background: isActive ? 'rgba(209,34,41,0.06)' : 'transparent',
                        color: isActive ? '#D12229' : '#1A1A1A',
                        fontWeight: isActive ? 600 : 400,
                        borderLeft: isActive ? '3px solid #D12229' : '3px solid transparent',
                      }}
                    >
                      <div className="truncate">{t.name}</div>
                      <div className="text-xs font-mono truncate mt-0.5" style={{ color: isActive ? '#D12229' : '#6B6B70', opacity: 0.8 }}>
                        {t.slug}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Users table */}
        <div className="flex-1 min-w-0 bg-white rounded-xl" style={{ border: '1px solid #E5E5E8' }}>
          <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid #E5E5E8' }}>
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md"
              style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
            >
              <i className="ri-search-line text-sm" style={{ color: '#6B6B70' }}></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="bg-transparent outline-none text-sm flex-1"
                style={{ color: '#1A1A1A' }}
              />
            </div>
            <button
              onClick={() => selectedTenant && loadUsers(selectedTenant)}
              disabled={!selectedTenant || usersLoading}
              className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors disabled:opacity-40"
              style={{ border: '1px solid #E5E5E8' }}
              title="Refresh"
            >
              <i className={`ri-refresh-line text-sm ${usersLoading ? 'animate-spin' : ''}`} style={{ color: '#6B6B70' }}></i>
            </button>
          </div>

          {!selectedTenant ? (
            <div className="p-12 text-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ background: '#F5F5F7' }}
              >
                <i className="ri-building-line text-xl" style={{ color: '#6B6B70' }}></i>
              </div>
              <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Select a tenant</p>
              <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>Choose a tenant from the left to view its users</p>
            </div>
          ) : usersLoading ? (
            <div className="p-12 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
              <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>Loading users...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ background: '#F5F5F7' }}
              >
                <i className="ri-team-line text-xl" style={{ color: '#6B6B70' }}></i>
              </div>
              <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>No users found</p>
              <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>
                {search ? 'Try a different search term' : `No users assigned to ${selectedTenant.name} yet`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#F5F5F7' }}>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>User</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Role</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Added</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tu) => {
                    const roleStyle = getRoleBadgeStyle(tu.role);
                    const isChanging = changingRole === tu.id;
                    return (
                      <tr key={tu.id} style={{ borderBottom: '1px solid #F5F5F7' }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                              style={{ background: '#D12229' }}
                            >
                              {(tu.user.fullName || '?').slice(0, 1).toUpperCase()}
                            </div>
                            <span className="font-medium" style={{ color: '#1A1A1A' }}>{tu.user.fullName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{tu.user.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {isChanging && (
                              <i className="ri-loader-4-line animate-spin text-sm" style={{ color: '#6B6B70' }}></i>
                            )}
                            <select
                              value={tu.role}
                              disabled={isChanging}
                              onChange={(e) => handleRoleChange(tu.id, e.target.value as Role)}
                              className="text-xs font-medium px-2.5 py-1 rounded-full cursor-pointer outline-none disabled:opacity-60"
                              style={{
                                background: roleStyle.bg,
                                color: roleStyle.color,
                                border: `1px solid ${roleStyle.color}30`,
                              }}
                            >
                              {ROLES.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                          {tu.createdAt ? new Date(tu.createdAt).toLocaleDateString('uk-UA') : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                          {tu.updatedAt ? new Date(tu.updatedAt).toLocaleDateString('uk-UA') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
